/**
 * Compatibility between the legacy 9 landlord permission names and the
 * Permission Master catalog. Pure / client-safe.
 *
 * Phase 5: Bucket A (dead junk names) is cleaned from landlord_org DBs.
 * This expand shim stays until custom-role UI (`PermissionMatrixEditor`)
 * grants the full master catalog (Bucket B) — otherwise custom roles that
 * only store the coarse 9 would lose implied fine-grained access.
 */

/** Dead landlord_org permission names — never used by auth guards. */
export const DEAD_LANDLORD_LEGACY_PERMISSION_NAMES = [
	'property_management',
	'tenant_screening',
	'application_review',
	'reports_access',
] as const

/** Coarse keys that still appear in the custom-role matrix UI. */
export const LEGACY_LANDLORD_PERMISSION_KEYS = [
	'applications.approve',
	'applications.reject',
	'applications.withdraw',
	'applications.delete',
	'applications.request_info',
	'applications.merge',
	'properties.manage',
	'data.export',
	'users.edit_assign',
	'roles.manage',
	'settings.billing.view',
	'settings.notifications.own',
	'settings.app_form.view',
	'settings.app_form.edit_optional_question',
	'settings.app_form.edit_documents',
	'settings.app_form.set_min_rental_history',
] as const

export type LegacyLandlordPermissionKey =
	(typeof LEGACY_LANDLORD_PERMISSION_KEYS)[number]

/**
 * If the user holds `legacy`, treat these master keys as also granted.
 * Includes the legacy key itself so expansion is idempotent.
 */
export const LEGACY_TO_MASTER_EXPAND: Record<string, readonly string[]> = {
	'applications.approve': [
		'applications.approve',
		'applications.view',
		'applications.view_score',
		'applications.view_combined_score',
		'nav.applications',
	],
	'applications.reject': [
		'applications.reject',
		'applications.view',
		'applications.view_score',
		'applications.view_combined_score',
		'nav.applications',
	],
	'applications.withdraw': [
		'applications.withdraw',
		'applications.reinstate',
		'applications.view',
		'nav.applications',
	],
	'applications.delete': [
		'applications.delete',
		'applications.view',
		'nav.applications',
	],
	'applications.request_info': [
		'applications.request_info',
		'applications.request_bank',
		'applications.request_guarantor',
		'applications.view',
		'nav.applications',
	],
	'applications.merge': [
		'applications.merge',
		'applications.view',
		'nav.applications',
	],
	'properties.manage': [
		'properties.manage',
		'properties.view',
		'properties.add',
		'properties.edit',
		'properties.delete',
		'nav.properties',
	],
	'data.export': [
		'data.export',
		'download.report_pdf',
		'download.document',
		'download.bulk',
		'settings.billing.export_csv',
	],
	'users.edit_assign': [
		'users.edit_assign',
		'settings.team.view',
		'settings.team.invite',
		'settings.team.edit',
		'settings.team.deactivate',
		'settings.team.manage_invites',
	],
	'roles.manage': [
		'roles.manage',
		'settings.roles.view',
		'settings.roles.set_property_assignment',
	],
	// view must NOT expand to purchase_credits — Excel separates money-moving
	// from read-only billing access (PM may hold both via role grants).
	'settings.billing.view': ['settings.billing.view', 'settings.payment.view'],
	'settings.notifications.own': ['settings.notifications.own'],
	'settings.app_form.edit_optional_question': [
		'settings.app_form.edit_optional_question',
		'settings.app_form.view',
	],
	'settings.app_form.edit_documents': [
		'settings.app_form.edit_documents',
		'settings.app_form.view',
	],
	'settings.app_form.set_min_rental_history': [
		'settings.app_form.set_min_rental_history',
		'settings.app_form.view',
	],
}

/** Master key → legacy keys that satisfy it (for reverse checks). */
const MASTER_SATISFIED_BY_LEGACY: Record<string, readonly string[]> = (() => {
	const out: Record<string, string[]> = {}
	for (const [legacy, masters] of Object.entries(LEGACY_TO_MASTER_EXPAND)) {
		for (const master of masters) {
			if (!out[master]) out[master] = []
			if (!out[master].includes(legacy)) out[master].push(legacy)
		}
	}
	return out
})()

/**
 * Expand a granted permission name list with master keys implied by legacy
 * grants. Deduped; order not significant.
 */
export function expandLegacyPermissionNames(
	granted: readonly string[],
): string[] {
	const set = new Set<string>(granted)
	for (const name of granted) {
		const extras = LEGACY_TO_MASTER_EXPAND[name]
		if (!extras) continue
		for (const key of extras) set.add(key)
	}
	return [...set]
}

/**
 * Whether `granted` satisfies `needed`, honoring legacy ↔ master compatibility.
 */
export function hasCompatiblePermission(
	granted: readonly string[],
	needed: string,
): boolean {
	if (granted.includes('full_access')) return true
	if (granted.includes(needed)) return true

	const expanded = expandLegacyPermissionNames(granted)
	if (expanded.includes(needed)) return true

	const legacySatisfiers = MASTER_SATISFIED_BY_LEGACY[needed]
	if (legacySatisfiers?.some((k) => granted.includes(k))) return true

	return false
}
